import { z } from 'zod';

const specDisplayFieldSchema = z
  .object({
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().trim().min(1),
    type: z.enum(['string', 'number', 'boolean']),
    unit: z.string().trim().min(1).optional(),
  })
  .strict();

export const specDisplaySchema = z
  .object({
    version: z.literal(1),
    fields: z.array(specDisplayFieldSchema),
  })
  .strict()
  .superRefine(({ fields }, ctx) => {
    const seen = new Set<string>();
    fields.forEach((field, index) => {
      if (seen.has(field.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate spec display key: ${field.key}`,
          path: ['fields', index, 'key'],
        });
      }
      seen.add(field.key);
    });
  });

export type SpecDisplaySchema = z.infer<typeof specDisplaySchema>;
export type SpecFact = string | number | boolean;
export interface SpecDisplayRow {
  key: string;
  label: string;
  value: string;
}

export type SpecDisplayResult =
  | { status: 'ok'; rows: SpecDisplayRow[] }
  | { status: 'unavailable'; message: 'Spec display configuration unavailable.' };

export function renderSpecDisplay(
  schema: unknown,
  facts: Record<string, unknown> | null | undefined,
): SpecDisplayResult {
  const parsed = specDisplaySchema.safeParse(schema);
  if (!parsed.success) {
    return { status: 'unavailable', message: 'Spec display configuration unavailable.' };
  }

  const rows = parsed.data.fields.map((field) => {
    const raw = facts?.[field.key];
    let value = '—';
    if (field.type === 'boolean' && typeof raw === 'boolean') {
      value = raw ? 'Yes' : 'No';
    } else if (field.type === 'number' && typeof raw === 'number' && Number.isFinite(raw)) {
      value = String(raw);
      if (field.unit) value += ` ${field.unit}`;
    } else if (field.type === 'string' && typeof raw === 'string' && raw.trim()) {
      value = raw.trim();
    }
    return { key: field.key, label: field.label, value };
  });

  return { status: 'ok', rows };
}
